import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { OrganisationType } from 'src/app/model/enums/organisationType';
import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { OrganisationService } from '../dataServices/organisation-service/organisationservice';
import { UserService } from '../user-service/userservice';

@Injectable({
    providedIn: 'root'
})
export class OrganisationTypeGuard implements CanActivate, CanActivateChild {
    constructor(
        private readonly organisationService: OrganisationService,
        private readonly router: Router,
        private readonly userService: UserService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean | UrlTree> {
        return this.canAccess(route, state);
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean | UrlTree> {
        return this.canAccess(childRoute, state);
    }

    private async canAccess(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean | UrlTree> {
        if (this.userService.isCurrentUserPlatformAdmin()) {
            return true;
        }

        const requiredTypes = this.getRequiredOrganisationTypes(state.url);
        if (requiredTypes.length === 0) {
            return true;
        }

        const organisationId = this.getOrganisationId(route);
        if (organisationId == null) {
            return this.router.parseUrl('/marktplatz');
        }

        const organisation = await this.loadOrganisation(organisationId);
        if (!organisation) {
            return this.router.parseUrl('/marktplatz');
        }

        const organisationTypes = organisation.organisationType ?? [];
        const hasRequiredType = requiredTypes.some((requiredType) =>
            organisationTypes.includes(requiredType)
        );

        if (hasRequiredType) {
            return true;
        }

        return this.router.createUrlTree([
            '/organisations',
            organisationId,
            'information'
        ]);
    }

    private async loadOrganisation(
        organisationId: number
    ): Promise<OrganisationInterface | null> {
        try {
            return await this.organisationService.getItem(organisationId);
        } catch {
            return null;
        }
    }

    private getOrganisationId(route: ActivatedRouteSnapshot): number | null {
        for (const snapshot of [...route.pathFromRoot].reverse()) {
            const organisationId = snapshot.paramMap.get('organisationId');
            if (organisationId != null) {
                const parsedOrganisationId = Number(organisationId);
                if (!Number.isNaN(parsedOrganisationId)) {
                    return parsedOrganisationId;
                }
            }
        }
        return null;
    }

    private getRequiredOrganisationTypes(
        url: string
    ): OrganisationType[] {
        const sanitizedUrl = url.split('?')[0].split('#')[0];

        const productRelatedRoutes = [
            /\/organisations\/\d+\/products(\/|$)/,
            /\/organisations\/\d+\/firstProduct(\/|$)/,
            /\/organisations\/\d+\/zahlung(\/|$)/,
            /\/organisations\/\d+\/rechnungen(\/|$)/,
            /\/organisations\/\d+\/abonnements(\/|$)/,
            /\/organisations\/\d+\/aboPayment(\/|$)/,
            /\/organisations\/\d+\/sdFactory(\/|$)/,
            /\/organisations\/\d+\/construct-x-settings(\/|$)/,
            /\/organisations\/\d+\/data-space-onboarding(\/|$)/,
            /\/organisations\/\d+\/wallet(\/|$)/,
            /\/organisations\/\d+\/did-manager(\/|$)/
        ];

        const certificationRelatedRoutes = [
            /\/organisations\/\d+\/certifications(\/|$)/,
            /\/organisations\/\d+\/certificationRequests(\/|$)/
        ];

        const ptgRelatedRoutes = [
            /\/organisations\/\d+\/productTypeGroups(\/|$)/,
            /\/organisations\/\d+\/features(\/|$)/
        ];

        if (productRelatedRoutes.some((pattern) => pattern.test(sanitizedUrl))) {
            return [
                OrganisationType.PRODUCT_PROVIDER,
                OrganisationType.DATASPACE_PARTICIPANT
            ];
        }

        if (
            certificationRelatedRoutes.some((pattern) => pattern.test(sanitizedUrl))
        ) {
            return [
                OrganisationType.CERTIFICATION_PROVIDER,
                OrganisationType.CERTIFICATION_OFFICE
            ];
        }

        if (ptgRelatedRoutes.some((pattern) => pattern.test(sanitizedUrl))) {
            return [OrganisationType.PRODUCT_TYPE_ASSOCIATION];
        }

        return [];
    }
}
